import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ExcelRenderer } from './ExcelRenderer'
import { PdfRenderer } from './PdfRenderer'
import { useDomHighlight } from '../useDomHighlight'

interface SheetPreviewProps {
  fileUrl: string
  fileName: string
  pdfDefaultFitMode?: 'width' | 'page'
  pdfInitialPage?: number
  onPdfPageChange?: (page: number) => void
  onNumPagesChange?: (numPages: number) => void
  searchTerm?: string
}

/** True when the bytes behind `url` are a PDF rather than the workbook itself. */
export async function holdsPdf(url: string): Promise<boolean> {
  const response = await fetch(url)
  const head = new Uint8Array(await (await response.blob()).slice(0, 5).arrayBuffer())
  return String.fromCharCode(...head) === '%PDF-'
}

/**
 * A workbook's preview (cdb_tqs#2162): the server's PDF with one page per sheet,
 * so charts, images and formatting look as they do in Excel. The sheet grid
 * draws what the caller holds when that is the workbook itself — an `.xls`, or
 * a conversion that failed and fell back to the file.
 */
export function SheetPreview({
  fileUrl,
  fileName,
  pdfDefaultFitMode,
  pdfInitialPage,
  onPdfPageChange,
  onNumPagesChange,
  searchTerm,
}: SheetPreviewProps) {
  const [format, setFormat] = useState<'pdf' | 'sheet' | null>(null)
  // The PDF marks its own text layer; the grid is parsed later and redrawn per
  // sheet, so its hits are kept marked while it changes.
  const gridRef = useRef<HTMLDivElement>(null)
  useDomHighlight(gridRef, format === 'sheet' ? (searchTerm ?? '') : '')

  useEffect(() => {
    let cancelled = false
    setFormat(null)
    holdsPdf(fileUrl)
      .then((pdf) => !cancelled && setFormat(pdf ? 'pdf' : 'sheet'))
      .catch(() => !cancelled && setFormat('sheet'))
    return () => {
      cancelled = true
    }
  }, [fileUrl])

  if (format === null) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }
  if (format === 'sheet') {
    return (
      <div ref={gridRef} className="flex min-h-0 flex-1 flex-col">
        <ExcelRenderer fileUrl={fileUrl} />
      </div>
    )
  }
  return (
    <PdfRenderer
      fileUrl={fileUrl}
      fileName={fileName}
      defaultFitMode={pdfDefaultFitMode}
      pageNumber={pdfInitialPage}
      onPageChange={onPdfPageChange}
      onNumPagesChange={onNumPagesChange}
      searchTerm={searchTerm}
      sheetLayout
    />
  )
}
